/**
 * FNV-1a state hashing. Used to prove two simulation runs are byte-identical.
 *
 * Floats go in through an exact bit reinterpretation, so `0.1 + 0.2` and `0.3`
 * hash differently — which is the entire point.
 */

const scratch = new ArrayBuffer(8);
const f64 = new Float64Array(scratch);
const u32 = new Uint32Array(scratch);

export class Hasher {
  private h = 2166136261 >>> 0;

  u32(v: number): this {
    let h = this.h;
    const x = v >>> 0;
    h ^= x & 0xff;
    h = Math.imul(h, 16777619);
    h ^= (x >>> 8) & 0xff;
    h = Math.imul(h, 16777619);
    h ^= (x >>> 16) & 0xff;
    h = Math.imul(h, 16777619);
    h ^= (x >>> 24) & 0xff;
    h = Math.imul(h, 16777619);
    this.h = h >>> 0;
    return this;
  }

  i32(v: number): this {
    return this.u32(v | 0);
  }

  /**
   * A float, by its exact bits — with one deliberate exception.
   *
   * Negative zero is folded onto positive zero. Everything else about this
   * hasher is a bit-exact reinterpretation, and that is the point; but `-0` is
   * the one value the *save format* cannot carry. `JSON.stringify(-0)` is
   * `"0"`, so a world holding a `-0` and the same world saved and reloaded are
   * identical to every consumer in the simulation — `-0 === 0`, and nothing
   * here ever divides by one of these — and yet hashed differently. The
   * round-trip test would fail on a world that was, in every way that can be
   * observed, the same world.
   *
   * This is not theoretical. `Math.atan2` and `rotateToward` both produce `-0`
   * readily, and a company's `facing` settles on it often enough that fourteen
   * of two hundred and fifty-five companies carried one after forty-five ticks.
   * At a dozen companies you could play for a long time and never see it; at
   * city scale it is certain. Determinism between two *runs* is untouched —
   * the same code produces the same `-0` in both — so the only thing this
   * gives up is the ability to distinguish two states that no save can tell
   * apart either.
   */
  num(v: number): this {
    f64[0] = v === 0 ? 0 : v;
    this.u32(u32[0]);
    this.u32(u32[1]);
    return this;
  }

  bool(v: boolean): this {
    return this.u32(v ? 1 : 0);
  }

  str(v: string): this {
    this.u32(v.length);
    for (let i = 0; i < v.length; i++) this.u32(v.charCodeAt(i));
    return this;
  }

  arrU32(v: ArrayLike<number>, count = v.length): this {
    this.u32(count);
    for (let i = 0; i < count; i++) this.u32(v[i] >>> 0);
    return this;
  }

  arrNum(v: ArrayLike<number>, count = v.length): this {
    this.u32(count);
    for (let i = 0; i < count; i++) this.num(v[i]);
    return this;
  }

  digest(): number {
    return this.h >>> 0;
  }

  hex(): string {
    return (this.h >>> 0).toString(16).padStart(8, '0');
  }
}

export function hashString(str: string): number {
  return new Hasher().str(str).digest();
}
